import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

export interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'error' | 'round' | 'text';
  type?: 'button' | 'submit';
  mode?: 'button' | 'link';
  disabled?: boolean;
  isInProgress?: boolean;
  inProgressText?: string;
  className?: string;
  onClick?: (event: any) => void;
  href?: string;
  target?: string;
  testId?: string;
}

const BUTTON_STYLES = {
  primary:
    'text-white bg-blue-600 border border-solid border-blue-600 px-3.5 py-2 rounded-lg',
  secondary:
    'bg-gray-300 text-neutral-800 border border-solid border-gray-200 px-3.5 py-2 rounded-lg',
  error:
    'bg-red-500 text-white border border-solid border-red-500 px-3.5 py-2 rounded-lg',
  round: 'bg-gray-500 border-none p-1 rounded-full',
  text: 'bg-transparent border-none p-0',
};

/**
 * Button component
 *
 * This component displays a button with a label and optional variant.
 */
const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  type = 'button',
  mode = 'button',
  href,
  target,
  className = '',
  isInProgress: isActionInProgress,
  inProgressText,
  onClick,
  disabled,
  testId,
}) => {
  const variantClassName = `flex-initial justify-center whitespace-nowrap ${BUTTON_STYLES[variant ?? 'primary']} ${
    disabled || isActionInProgress ? 'opacity-50 cursor-not-allowed' : ''
  }`;

  switch (mode) {
    case 'link':
      return (
        <a
          href={href}
          className={`${variantClassName} ${className}`}
          onClick={
            disabled || isActionInProgress ? (e) => e.preventDefault() : onClick
          }
          target={target}
          data-testid={testId}
        >
          {isActionInProgress ? (
            <>
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin h-5 w-5 mr-1 align-middle"
              />
              <span className="align-middle">{inProgressText}</span>
            </>
          ) : (
            children
          )}
        </a>
      );
    case 'button':
      return (
        <button
          disabled={disabled || isActionInProgress}
          className={`${variantClassName} ${className}`}
          onClick={onClick}
          type={type}
          data-testid={testId}
        >
          {isActionInProgress ? (
            <>
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin h-5 w-5 mr-1 align-middle"
              />
              <span className="align-middle">{inProgressText}</span>
            </>
          ) : (
            children
          )}
        </button>
      );
    default:
      throw new Error(
        `Unsupported button mode: ${mode}. Select either 'button' or 'link'.`,
      );
  }
};

export default Button;
